import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../services/api'
import toast from 'react-hot-toast'
import { useIsMobile } from '../hooks/useIsMobile'
import { format, parseISO } from 'date-fns'
import {
  Activity,
  ArrowRightLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  DollarSign,
  Handshake,
  Megaphone,
  RefreshCw,
  TrendingUp,
  Wallet,
  XCircle,
} from 'lucide-react'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts'
import SettlementNoticeBanner from '../components/SettlementNoticeBanner'
import PartnerOverview from '../components/partner-dashboard/PartnerOverview'
import { DEFAULT_DASHBOARD_RANGE } from '../components/partner-dashboard/model'
import { useAuth } from '../context/AppContext'
import { rangeProblem } from '../utils/dateRange'

// ─────────────────────────────────────────────────────────────────────────────
//   PARTNER DASHBOARD
//
//   Consume /api/partner/{affiliate,buyer}/* — la nueva API partner-facing.
//   Un usuario partner puede tener perfiles de afiliado, de comprador, ambos,
//   o (recién creado) ninguno todavía. Mostramos solo las tabs para las que
//   el usuario realmente tiene perfiles.
//
//   Deliberadamente NO mostramos qué comprador ganó un lead en la vista de
//   afiliado, ni qué afiliado originó un lead en la vista de comprador — esos
//   campos no vienen en el payload por diseño (evita filtrar relaciones entre
//   partners).
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d',    label: '7 days' },
  { id: '30d',   label: '30 days' },
  { id: 'ytd',   label: 'YTD' },
]

const DEFAULT_PRESET = '30d'

// The partner-facing status vocabulary, straight off the backend's
// PartnerLeadStatus enum. django-ninja validates it, so an unrecognised
// value is a 422 rather than a filter that quietly does nothing — which is
// why "all" has to mean *omit the param*, not send an empty one.
//
// Affiliate-only: /partner/buyer/leads takes no `status` at all (checked
// against the live OpenAPI schema, not assumed). Sending it there would be
// dropped without complaint and the table would silently stop agreeing
// with the selector, so the control isn't rendered on the buyer tab.
const STATUS_FILTERS = [
  { id: '',         label: 'All statuses' },
  { id: 'sold',     label: 'Sold' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'rejected', label: 'Rejected' },
]

// The endpoint's documented ceiling (OpenAPI: limit maximum 200). Used for
// the CSV walk — the on-screen table stays at PAGE_SIZE.
const EXPORT_PAGE_SIZE = 200

// Computed in UTC, not local time: the backend's apply_date_filter compares
// against UTC day boundaries, and date-fns' format()/subDays() work in the
// browser's local timezone — in any zone behind UTC that silently drops
// leads created earlier "today" in UTC terms (see AdminLeadsDashboard.jsx's
// similar-looking preset logic, which has the same latent mismatch).
function presetRange(p) {
  const now = new Date()
  const todayStr = now.toISOString().slice(0, 10)
  const utcDaysAgoStr = (days) => new Date(now.getTime() - days * 86400000).toISOString().slice(0, 10)
  if (p === 'today') return { dateFrom: todayStr, dateTo: todayStr }
  if (p === '7d') return { dateFrom: utcDaysAgoStr(6), dateTo: todayStr }
  if (p === '30d') return { dateFrom: utcDaysAgoStr(29), dateTo: todayStr }
  if (p === 'ytd') return { dateFrom: `${now.getUTCFullYear()}-01-01`, dateTo: todayStr }
  return { dateFrom: null, dateTo: null }
}

function PresetButton({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 11px', fontSize: 11, fontWeight: 600,
        borderRadius: 6, cursor: 'pointer',
        background: active ? 'var(--bg-raised)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        border: `1px solid ${active ? 'var(--border-strong)' : 'var(--border)'}`,
        transition: 'all .12s',
      }}
    >
      {label}
    </button>
  )
}

// Re-exported so existing imports (and Partnerdashboard.test.jsx) keep working.
export { rangeProblem }

const dateInputStyle = {
  padding: '4px 8px', fontSize: 11, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
}

// Presets plus (optionally) a free range. Shared by the campaign drilldown
// and the blocked-submissions widget, which keep entirely separate ranges.
function DateRangeControls({ preset, dateFrom, dateTo, onPreset, onRange = () => {}, allowCustom = false }) {
  const [draftFrom, setDraftFrom] = useState(dateFrom || '')
  const [draftTo, setDraftTo] = useState(dateTo || '')
  const [error, setError] = useState(null)

  // A preset rewrites the range, so the inputs follow it instead of still
  // showing whatever was typed before the button was clicked. Clearing the
  // error here is also what dismisses a bad range once a preset rescues it.
  useEffect(() => {
    setDraftFrom(dateFrom || '')
    setDraftTo(dateTo || '')
    setError(null)
  }, [dateFrom, dateTo])

  const commit = (from, to) => {
    setDraftFrom(from)
    setDraftTo(to)
    // One side still empty means mid-edit, not invalid — don't shout yet,
    // and don't fetch either.
    if (!from || !to) { setError(null); return }
    const problem = rangeProblem(from, to)
    setError(problem)
    if (!problem) onRange(from, to)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
        {DATE_PRESETS.map(p => (
          <PresetButton key={p.id} active={preset === p.id} label={p.label} onClick={() => onPreset(p.id)} />
        ))}
        {allowCustom && (
          <>
            <span style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
            <input
              type="date"
              aria-label="Start date"
              value={draftFrom}
              max={draftTo || undefined}
              onChange={e => commit(e.target.value, draftTo)}
              style={{
                ...dateInputStyle,
                borderColor: error ? 'var(--red)' : 'var(--border)',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>to</span>
            <input
              type="date"
              aria-label="End date"
              value={draftTo}
              min={draftFrom || undefined}
              onChange={e => commit(draftFrom, e.target.value)}
              style={{
                ...dateInputStyle,
                borderColor: error ? 'var(--red)' : 'var(--border)',
              }}
            />
          </>
        )}
      </div>
      {error && (
        <span role="alert" style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>
          {error}
        </span>
      )}
    </div>
  )
}

const STATUS_META = {
  sold: {
    label: 'Sold',
    color: 'var(--green)',
    bg: 'var(--green-bg)',
    icon: <DollarSign size={11} />,
  },
  accepted: {
    label: 'Accepted',
    color: 'var(--amber)',
    bg: 'var(--amber-bg)',
    icon: <CheckCircle2 size={11} />,
  },
  rejected: {
    label: 'Rejected',
    color: 'var(--red)',
    bg: 'var(--red-bg)',
    icon: <XCircle size={11} />,
  },
  new: {
    label: 'New',
    color: 'var(--blue)',
    bg: 'var(--blue-bg)',
    icon: null,
  },
}

function getStatusMeta(status) {
  return STATUS_META[status] || {
    label: status || '—',
    color: 'var(--text-muted)',
    bg: 'var(--bg-raised)',
    icon: null,
  }
}

const fmtDate = (ts) => {
  if (!ts) return '—'
  try { return format(parseISO(ts), 'MMM d, yyyy h:mm a') } catch { return '—' }
}

const fmtMoney = (v) =>
  (v === null || v === undefined || v === '')
    ? '—'
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const TOOLTIP_STYLE = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 11,
  color: 'var(--text-primary)',
  boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
}

// ════════════════════════════════════════════════════════════════════════════
//   STATUS PILL
// ════════════════════════════════════════════════════════════════════════════
function StatusPill({ status }) {
  const meta = getStatusMeta(status)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
      padding: '4px 9px', borderRadius: 4,
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.color}30`,
      fontFamily: 'ui-monospace, monospace',
    }}>
      {meta.icon}
      {meta.label}
    </span>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   STAT CARDS
// ════════════════════════════════════════════════════════════════════════════
function StatCard({ icon, label, value, color, loading, error }) {
  return (
    <div className="card" style={{
      padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
      flex: '1 1 200px', minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-raised)', color: error ? 'var(--red)' : color,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 800,
          color: error ? 'var(--red)' : 'var(--text-primary)', opacity: loading ? 0.4 : 1, lineHeight: 1.15,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {loading ? '—' : error ? 'Error' : value}
        </div>
        <div style={{
          fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase',
          letterSpacing: 0.5, fontWeight: 600, marginTop: 2,
        }}>
          {label}
        </div>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   STATUS BREAKDOWN (pie)
// ════════════════════════════════════════════════════════════════════════════
function StatusBreakdown({ by, error }) {
  const sold = by?.sold || 0
  const accepted = by?.accepted || 0
  const rejected = by?.rejected || 0
  const total = sold + accepted + rejected

  if (error) {
    return (
      <div style={{
        height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 8, color: 'var(--red)',
      }}>
        <XCircle size={20} style={{ opacity: 0.6 }} />
        <span style={{ fontSize: 12 }}>Couldn't load this data — try refreshing</span>
      </div>
    )
  }

  if (total === 0) {
    return (
      <div style={{
        height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 8, color: 'var(--text-muted)',
      }}>
        <Activity size={20} style={{ opacity: 0.35 }} />
        <span style={{ fontSize: 12 }}>No leads yet</span>
      </div>
    )
  }

  const data = [
    { name: 'Sold', value: sold, color: 'var(--green)' },
    { name: 'Accepted', value: accepted, color: 'var(--amber)' },
    { name: 'Rejected', value: rejected, color: 'var(--red)' },
  ].filter(d => d.value > 0)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <ResponsiveContainer width={150} height={150}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
            innerRadius={42} outerRadius={64} paddingAngle={2} strokeWidth={0}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip contentStyle={TOOLTIP_STYLE} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { name: 'Sold', value: sold, color: 'var(--green)' },
          { name: 'Accepted', value: accepted, color: 'var(--amber)' },
          { name: 'Rejected', value: rejected, color: 'var(--red)' },
        ].map(d => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 64 }}>{d.name}</span>
            <span style={{
              fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
              fontFamily: 'ui-monospace, monospace',
            }}>{d.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   PAGINATION
// ════════════════════════════════════════════════════════════════════════════
function Pagination({ page, totalPages, total, limit, onPageChange }) {
  if (!totalPages || totalPages <= 1) return null
  const start = (page - 1) * limit + 1
  const end = Math.min(page * limit, total)
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 16px', background: 'var(--bg-surface)',
      border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Showing <strong style={{ color: 'var(--text-primary)' }}>{start}–{end}</strong> of{' '}
        <strong style={{ color: 'var(--text-primary)' }}>{total.toLocaleString()}</strong> leads
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="btn btn-ghost btn-sm"
          aria-label="Previous page"
          style={{ padding: '4px 8px', opacity: page === 1 ? 0.4 : 1 }}
        >
          <ChevronLeft size={14} />
        </button>
        <span style={{
          fontFamily: 'ui-monospace, monospace', fontSize: 12,
          color: 'var(--text-secondary)', minWidth: 60, textAlign: 'center',
        }}>
          {page} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="btn btn-ghost btn-sm"
          aria-label="Next page"
          style={{ padding: '4px 8px', opacity: page === totalPages ? 0.4 : 1 }}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   LEADS TABLE
// ════════════════════════════════════════════════════════════════════════════
function LeadsTable({ leads, moneyLabel, moneyKey, loading, error, isMobile }) {
  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
        Loading leads…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--red)', fontSize: 12 }}>
        Couldn't load leads — try refreshing.
      </div>
    )
  }

  if (!leads || leads.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
        No leads yet.
      </div>
    )
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12 }}>
        {leads.map(l => (
          <div key={l.kickoff_lead_id} className="card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 700,
                color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{l.kickoff_lead_id}</span>
              <StatusPill status={l.partner_status} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(l.createdAt)}</span>
              <span style={{
                fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
                fontFamily: 'ui-monospace, monospace',
              }}>{fmtMoney(l[moneyKey])}</span>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
        <thead>
          <tr>
            {['Lead ID', 'Status', 'Date', moneyLabel].map(h => (
              <th key={h} style={{
                textAlign: h === moneyLabel ? 'right' : 'left',
                padding: '10px 14px', fontSize: 10, fontWeight: 700,
                letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {leads.map(l => (
            <tr key={l.kickoff_lead_id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{
                padding: '10px 14px', fontSize: 12, fontFamily: 'ui-monospace, monospace',
                color: 'var(--text-primary)',
              }}>{l.kickoff_lead_id}</td>
              <td style={{ padding: '10px 14px' }}><StatusPill status={l.partner_status} /></td>
              <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>
                {fmtDate(l.createdAt)}
              </td>
              <td style={{
                padding: '10px 14px', fontSize: 12, fontWeight: 700, textAlign: 'right',
                color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
              }}>{fmtMoney(l[moneyKey])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   CAMPAIGN LIST — landing view for the affiliate tab. Partners are
//   campaign-based now (used to be single-integration), so leads/stats are
//   scoped to one campaign at a time instead of pooled across all of them.
// ════════════════════════════════════════════════════════════════════════════
const DELIVERY_TYPE_LABELS = { api: 'API', clickout: 'Clickout' }

function CampaignCard({ c, onClick }) {
  const [hover, setHover] = useState(false)
  const badgeText = c.vertical || DELIVERY_TYPE_LABELS[c.delivery_type] || 'No vertical'
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="card"
      style={{
        padding: 20, display: 'flex', flexDirection: 'column', gap: 16,
        textAlign: 'left', width: '100%', cursor: 'pointer',
        borderColor: hover ? 'var(--accent)' : undefined,
        boxShadow: hover ? 'var(--shadow-lg)' : undefined,
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'all var(--transition)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: 'var(--accent-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--accent)', flexShrink: 0,
        }}>
          <Megaphone size={18} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
            {c.name}
          </div>
          <span className="badge badge-gray" style={{ marginTop: 6 }}>
            {badgeText}
          </span>
        </div>
        <span className={`badge ${c.is_active !== false ? 'badge-green' : 'badge-red'}`} style={{ flexShrink: 0 }}>
          {c.is_active !== false ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: 14, borderTop: '1px solid var(--border)',
      }}>
        <div>
          <div style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 22, fontWeight: 800,
            color: 'var(--text-primary)', lineHeight: 1.15,
          }}>
            {(c.lead_count ?? 0).toLocaleString()}
          </div>
          <div style={{
            fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase',
            letterSpacing: 0.5, fontWeight: 600, marginTop: 2,
          }}>
            {c.lead_count === 1 ? 'Lead' : 'Leads'}
          </div>
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600,
          color: hover ? 'var(--accent)' : 'var(--text-muted)', transition: 'color var(--transition)',
        }}>
          View leads <ChevronRight size={14} />
        </span>
      </div>
    </button>
  )
}

function CampaignListView({
  endpoint = '/partner/affiliate/campaigns',
  includeTestLeads = false,
  onSelect,
  showNotices = true,
}) {
  const isMobile = useIsMobile()
  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchCampaigns = useCallback(() => {
    setLoading(true)
    setError(false)
    api.get(endpoint, { params: includeTestLeads ? { include_test_leads: true } : {} })
      .then(({ data }) => setCampaigns(Array.isArray(data) ? data : []))
      .catch(() => {
        setError(true)
        toast.error('Could not load campaigns')
      })
      .finally(() => setLoading(false))
  }, [endpoint, includeTestLeads])

  useEffect(() => { fetchCampaigns() }, [fetchCampaigns])

  // Rendered as a section rather than returned early, so the notice banner
  // above it still shows in every state.
  const campaignSection = () => {
    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Loading your campaigns…
        </div>
      )
    }

    if (error) {
      return (
        <div className="card" style={{
          padding: 40, textAlign: 'center', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <XCircle size={28} style={{ color: 'var(--red)', opacity: 0.7 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Couldn't load your campaigns
          </span>
          <button className="btn btn-ghost btn-sm" onClick={fetchCampaigns}>
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )
    }

    if (campaigns.length === 0) {
      return (
        <div className="card" style={{
          padding: 40, textAlign: 'center', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <Megaphone size={28} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            No campaigns yet
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 360 }}>
            Reach out to your account manager to get a campaign set up.
          </span>
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
          color: 'var(--text-muted)', textTransform: 'uppercase',
        }}>
          {campaigns.length} {campaigns.length === 1 ? 'campaign' : 'campaigns'}
        </div>
        <div style={{
          display: 'grid', gap: 14,
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 340px))',
        }}>
          {campaigns.map(c => (
            <CampaignCard key={c.id} c={c} onClick={() => onSelect(c.id)} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* No range: `/partner/{role}/campaigns` takes no date parameter, so the
          cards below are not filtered to a period and neither is this.
          Turned off on the affiliate landing view, where the dashboard above
          already renders the notices for its own range - showing them twice
          would also show them for two different periods. */}
      {showNotices && <SettlementNoticeBanner />}
      {campaignSection()}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   CSV EXPORT
//
//   Columns are exactly the four the table renders and nothing else. This is
//   an external-facing view, so anything extra that leaked in here — the
//   winning buyer, another partner's data, the gross or our margin — would be
//   a disclosure, not a nicer export.
// ════════════════════════════════════════════════════════════════════════════
function csvEscape(v) {
  if (v === null || v === undefined) return ''
  const s = String(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

const csvDate = (ts) => {
  if (!ts) return ''
  // Sortable rather than the screen's "Oct 7, 2026 3:45 PM": a spreadsheet sorts this
  // correctly and can't read it as an American date. Same format the
  // rejected-submissions export already writes.
  try { return format(parseISO(ts), 'yyyy-MM-dd HH:mm:ss') } catch { return '' }
}

const csvMoney = (v) =>
  (v === null || v === undefined || v === '') ? '' : Number(v).toFixed(2)

export function buildLeadsCsv(rows, moneyLabel, moneyKey) {
  const headers = ['Lead ID', 'Status', 'Date', moneyLabel]
  const body = rows.map(l => [
    l.kickoff_lead_id,
    getStatusMeta(l.partner_status).label,
    csvDate(l.createdAt),
    csvMoney(l[moneyKey]),
  ].map(csvEscape).join(',')).join('\n')
  return `${headers.map(csvEscape).join(',')}\n${body}`
}

function downloadCsv(csv, filename) {
  // Leading BOM so Excel reads it as UTF-8 instead of mangling accented
  // names into mojibake.
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ════════════════════════════════════════════════════════════════════════════
//   ROLE PANEL — shared shell for affiliate & buyer tabs
// ════════════════════════════════════════════════════════════════════════════
function RolePanel({ role, campaignId, includeTestLeads = false, onBack }) {
  // role: 'affiliate' | 'buyer'
  const isAffiliate = role === 'affiliate'
  const campaignParamKey = isAffiliate ? 'campaign_id' : 'buyer_campaign_id'
  const moneyLabel = isAffiliate ? 'Payout' : 'Price paid'
  const moneyKey = isAffiliate ? 'payout_amount' : 'price_paid'
  const summaryMoneyKey = isAffiliate ? 'total_payout' : 'total_spend'
  const summaryMoneyLabel = isAffiliate ? 'Total payout' : 'Total spend'

  const isMobile = useIsMobile()
  const [summary, setSummary] = useState(null)
  const [leadsResp, setLeadsResp] = useState({ leads: [], total: 0, page: 1, total_pages: 1, limit: PAGE_SIZE })
  const [page, setPage] = useState(1)
  const [loadingLeads, setLoadingLeads] = useState(true)
  const [loadingSummary, setLoadingSummary] = useState(true)
  const [errorSummary, setErrorSummary] = useState(false)
  const [errorLeads, setErrorLeads] = useState(false)
  const [preset, setPreset] = useState(DEFAULT_PRESET)
  const [dateFrom, setDateFrom] = useState(() => presetRange(DEFAULT_PRESET).dateFrom)
  const [dateTo, setDateTo] = useState(() => presetRange(DEFAULT_PRESET).dateTo)
  const [campaignMeta, setCampaignMeta] = useState(null)
  // '' = every status, sent by omitting the param entirely (see STATUS_FILTERS).
  const [status, setStatus] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 })
  const cancelExportRef = useRef(false)

  const applyPreset = useCallback((p) => {
    const { dateFrom: from, dateTo: to } = presetRange(p)
    setDateFrom(from); setDateTo(to)
    setPreset(p)
  }, [])

  // Only ever called with a range DateRangeControls has already validated,
  // so nothing malformed can reach the params below. Clearing `preset` is
  // what un-highlights the preset buttons for a hand-picked range.
  const applyRange = useCallback((from, to) => {
    setDateFrom(from); setDateTo(to)
    setPreset(null)
  }, [])

  const basePath = `/partner/${role}`

  // The leads/summary endpoints don't echo back the campaign's own name, so
  // it's fetched from the campaign list and matched by id — there's no
  // single-campaign-by-id endpoint. Without this, the drilldown view has no
  // way to show which campaign (or even which side, buyer vs affiliate)
  // you're looking at.
  useEffect(() => {
    if (!campaignId) {
      setCampaignMeta(null)
      return
    }
    let cancelled = false
    api.get(`${basePath}/campaigns`)
      .then(({ data }) => {
        if (cancelled) return
        const match = (Array.isArray(data) ? data : []).find(c => String(c.id) === String(campaignId))
        setCampaignMeta(match || null)
      })
      .catch(() => { if (!cancelled) setCampaignMeta(null) })
    return () => { cancelled = true }
  }, [basePath, campaignId])

  const fetchSummary = useCallback(() => {
    setLoadingSummary(true)
    setErrorSummary(false)
    const params = {
      ...(campaignId ? { [campaignParamKey]: campaignId } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      ...(includeTestLeads ? { include_test_leads: true } : {}),
    }
    api.get(`${basePath}/leads/summary`, { params })
      .then(({ data }) => setSummary(data))
      .catch(() => {
        setErrorSummary(true)
        toast.error(`Could not load ${role} summary`)
      })
      .finally(() => setLoadingSummary(false))
  }, [basePath, role, campaignId, campaignParamKey, dateFrom, dateTo, includeTestLeads])

  // One definition of "the active filter", shared by the table and the CSV
  // walk so an export can't quietly disagree with what is on screen.
  //
  // `status` is affiliate-only and omitted when empty — the buyer endpoint
  // has no such param, and the backend 422s on any value outside the enum.
  const leadFilterParams = useMemo(() => ({
    ...(campaignId ? { [campaignParamKey]: campaignId } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
    ...(isAffiliate && status ? { status } : {}),
    ...(includeTestLeads ? { include_test_leads: true } : {}),
  }), [campaignId, campaignParamKey, dateFrom, dateTo, isAffiliate, status, includeTestLeads])

  const fetchLeads = useCallback((p) => {
    setLoadingLeads(true)
    setErrorLeads(false)
    const params = { ...leadFilterParams, page: p, limit: PAGE_SIZE }
    api.get(`${basePath}/leads`, { params })
      .then(({ data }) => setLeadsResp({
        leads: data.leads || [],
        total: data.total ?? 0,
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        limit: data.limit || PAGE_SIZE,
      }))
      .catch(() => {
        setErrorLeads(true)
        toast.error(`Could not load ${role} leads`)
      })
      .finally(() => setLoadingLeads(false))
  }, [basePath, role, leadFilterParams])

  useEffect(() => { setPage(1) }, [campaignId, dateFrom, dateTo, includeTestLeads, status])
  useEffect(() => { fetchSummary() }, [fetchSummary])
  useEffect(() => { fetchLeads(page) }, [fetchLeads, page])

  const handleRefresh = () => {
    fetchSummary()
    fetchLeads(page)
  }

  // Walks every page of the active filter — deliberately uncapped, so a
  // partner with a year of history gets a year of history. Termination
  // comes from total_pages plus the empty-batch guard, never from a row
  // limit, and progress is reported per page so a long export doesn't look
  // like a hung button.
  const handleExport = async () => {
    if (exporting) return
    cancelExportRef.current = false
    setExporting(true)
    setExportProgress({ done: 0, total: leadsResp.total || 0 })

    const rows = []
    try {
      let p = 1
      let totalPages = 1
      do {
        const { data } = await api.get(`${basePath}/leads`, {
          params: { ...leadFilterParams, page: p, limit: EXPORT_PAGE_SIZE },
        })
        if (cancelExportRef.current) break
        const batch = data.leads || []
        rows.push(...batch)
        totalPages = data.total_pages || 1
        setExportProgress({ done: rows.length, total: data.total ?? rows.length })
        // An empty page ends the walk whatever total_pages claims: with no
        // row cap, this is what guarantees the loop terminates.
        if (batch.length === 0) break
        p += 1
      } while (p <= totalPages && !cancelExportRef.current)

      if (cancelExportRef.current) {
        toast('Export cancelled')
        return
      }
      if (rows.length === 0) {
        toast.error('No leads to export for these filters')
        return
      }
      const scope = campaignId ? `campaign-${campaignId}` : 'all-campaigns'
      const statusPart = (isAffiliate && status) ? `-${status}` : ''
      downloadCsv(
        buildLeadsCsv(rows, moneyLabel, moneyKey),
        `${role}-leads-${scope}${statusPart}-${dateFrom || 'start'}_${dateTo || 'today'}.csv`,
      )
      toast.success(`Exported ${rows.length.toLocaleString()} leads`)
    } catch {
      toast.error('Export failed — try again')
    } finally {
      setExporting(false)
    }
  }

  // Unmounting mid-export (back to campaigns, tab switch) has to stop the
  // walk; otherwise it keeps paging and then setState()s into a dead tree.
  useEffect(() => () => { cancelExportRef.current = true }, [])

  const acceptance = summary?.acceptance_pct ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* This panel's own range - the one DateRangeControls below drives, and
          the one the stat cards and the leads table are filtered to. */}
      <SettlementNoticeBanner dateFrom={dateFrom} dateTo={dateTo} />

      {campaignId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className={`badge ${isAffiliate ? 'badge-blue' : 'badge-purple'}`} style={{ flexShrink: 0 }}>
            {isAffiliate ? 'Affiliate campaign' : 'Buyer campaign'}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
            {campaignMeta ? campaignMeta.name : `Campaign #${campaignId}`}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        {onBack ? (
          <button className="btn btn-ghost btn-sm" onClick={onBack}>
            <ChevronLeft size={14} /> Back to campaigns
          </button>
        ) : <span />}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* Buyer leads take no `status` — rendering this there would be a
              control that looks like it works and doesn't. */}
          {isAffiliate && (
            <select
              className="select"
              aria-label="Filter leads by status"
              value={status}
              onChange={e => setStatus(e.target.value)}
              style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, width: 'auto' }}
            >
              {STATUS_FILTERS.map(f => (
                <option key={f.id || 'all'} value={f.id}>{f.label}</option>
              ))}
            </select>
          )}
          <DateRangeControls
            preset={preset}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onPreset={applyPreset}
            onRange={applyRange}
            allowCustom
          />
        </div>
      </div>
      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <StatCard
          icon={<Activity size={18} />}
          label="Total leads"
          value={(summary?.total ?? 0).toLocaleString()}
          color="var(--text-primary)"
          loading={loadingSummary}
          error={errorSummary}
        />
        <StatCard
          icon={<TrendingUp size={18} />}
          label="Acceptance rate"
          value={`${acceptance}%`}
          color={acceptance > 50 ? 'var(--green)' : acceptance > 30 ? 'var(--amber)' : 'var(--red)'}
          loading={loadingSummary}
          error={errorSummary}
        />
        <StatCard
          icon={isAffiliate ? <Wallet size={18} /> : <Handshake size={18} />}
          label={summaryMoneyLabel}
          value={fmtMoney(summary?.[summaryMoneyKey])}
          color="var(--green)"
          loading={loadingSummary}
          error={errorSummary}
        />
      </div>

      {/* Status breakdown */}
      <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
            color: 'var(--text-muted)', textTransform: 'uppercase',
          }}>Status breakdown</span>
          <button onClick={handleRefresh} className="btn btn-ghost btn-sm">
            <RefreshCw size={12} />
            Refresh
          </button>
        </div>
        <StatusBreakdown by={summary?.by_status} error={errorSummary} />
      </div>

      {/* Leads table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <ArrowRightLeft size={13} style={{ color: 'var(--text-muted)' }} />
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            color: 'var(--text-muted)', textTransform: 'uppercase',
          }}>Leads</span>
          {/* The summary endpoint takes no status, so the cards and donut
              above always cover every status. Said out loud rather than left
              for the partner to notice the numbers disagreeing. */}
          {isAffiliate && status && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {getStatusMeta(status).label} only — the totals above cover every status
            </span>
          )}
          <span style={{ flex: 1 }} />
          {exporting && (
            <span style={{
              fontSize: 11, color: 'var(--text-muted)',
              fontFamily: 'ui-monospace, monospace',
            }}>
              {exportProgress.done.toLocaleString()}
              {exportProgress.total ? ` / ${exportProgress.total.toLocaleString()}` : ''}
            </span>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExport}
            disabled={exporting || leadsResp.total === 0}
            style={{ opacity: (exporting || leadsResp.total === 0) ? 0.6 : 1 }}
          >
            <Download size={12} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          {exporting && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { cancelExportRef.current = true }}
            >
              Cancel
            </button>
          )}
        </div>
        <LeadsTable
          leads={leadsResp.leads}
          moneyLabel={moneyLabel}
          moneyKey={moneyKey}
          loading={loadingLeads}
          error={errorLeads}
          isMobile={isMobile}
        />
      </div>

      <Pagination
        page={leadsResp.page || page}
        totalPages={leadsResp.total_pages}
        total={leadsResp.total}
        limit={leadsResp.limit || PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
//   MAIN
// ════════════════════════════════════════════════════════════════════════════
export default function PartnerDashboard() {
  const { campaignId, buyerCampaignId } = useParams()
  const navigate = useNavigate()
  const { user, impersonation } = useAuth() || {}
  const partnerName = (impersonation?.active && impersonation.company_name) || user?.company_name || null
  const [hasAffiliate, setHasAffiliate] = useState(null) // null = loading, bool once known
  const [hasBuyer, setHasBuyer] = useState(null)
  const [activeTab, setActiveTab] = useState(null)
  const [profilesError, setProfilesError] = useState(false)
  // Shared across the campaign list and its drilldown (RolePanel) so the
  // counts on the list and the summary/leads once you click in never
  // disagree — this has to gate every one of the calls the current view
  // makes, not just one.
  const [includeTestLeads, setIncludeTestLeads] = useState(false)
  // The affiliate landing view's one range. Lives here rather than in
  // PartnerOverview because its control sits in the page header; every
  // block of the dashboard reads it through a single request.
  const [dashboardRange, setDashboardRange] = useState(DEFAULT_DASHBOARD_RANGE)

  useEffect(() => {
    api.get('/partner/affiliate/profiles')
      .then(({ data }) => setHasAffiliate(Array.isArray(data) && data.length > 0))
      .catch(() => {
        setHasAffiliate(false)
        setProfilesError(true)
      })

    api.get('/partner/buyer/profiles')
      .then(({ data }) => setHasBuyer(Array.isArray(data) && data.length > 0))
      .catch(() => {
        setHasBuyer(false)
        setProfilesError(true)
      })
  }, [])

  useEffect(() => {
    if (activeTab) return
    if (buyerCampaignId) { setActiveTab('buyer'); return }
    if (campaignId) { setActiveTab('affiliate'); return }
    // Wait for both profile checks to resolve before picking a default —
    // deciding off whichever one happens to come back first is a race that
    // can lock in the wrong tab for a dual-role partner.
    if (hasAffiliate === null || hasBuyer === null) return
    if (hasAffiliate) setActiveTab('affiliate')
    else if (hasBuyer) setActiveTab('buyer')
  }, [hasAffiliate, hasBuyer, activeTab, campaignId, buyerCampaignId])

  const loading = hasAffiliate === null || hasBuyer === null
  // The affiliate landing view is the workspace, which brings its own
  // header, range and sidebar. Everything else - the campaign lists, a
  // campaign's drilldown, the buyer side - keeps the page header below.
  const showWorkspace = !loading && hasAffiliate && activeTab === 'affiliate' && !campaignId && !buyerCampaignId

  if (showWorkspace) {
    return (
      <div className="fade-in">
        <PartnerOverview
          range={dashboardRange}
          onRangeChange={setDashboardRange}
          partnerName={partnerName}
          onOpenCampaigns={() => setActiveTab('campaigns')}
          onOpenBuyer={hasBuyer ? () => setActiveTab('buyer') : undefined}
        />
      </div>
    )
  }

  const tabs = [
    ...(hasAffiliate ? [{ id: 'affiliate', label: 'Overview' }, { id: 'campaigns', label: 'Campaigns' }] : []),
    ...(hasBuyer ? [{ id: 'buyer', label: 'Buyer' }] : []),
  ]

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h1>Partner Dashboard</h1>
        <p>Your leads and payouts, at a glance.</p>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Loading your account…
        </div>
      )}

      {!loading && !hasAffiliate && !hasBuyer && profilesError && (
        <div className="card" style={{
          padding: 40, textAlign: 'center', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <XCircle size={28} style={{ color: 'var(--red)', opacity: 0.7 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Couldn't load your account
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 360 }}>
            Something went wrong loading your profiles. Try refreshing the page.
          </span>
        </div>
      )}

      {!loading && !hasAffiliate && !hasBuyer && !profilesError && (
        <div className="card" style={{
          padding: 40, textAlign: 'center', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <Handshake size={28} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            No profiles yet
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 360 }}>
            Your company doesn't have any affiliate or buyer profiles set up yet.
            Reach out to your account manager to get started.
          </span>
        </div>
      )}

      {!loading && (hasAffiliate || hasBuyer) && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            {tabs.length > 1 && !campaignId && !buyerCampaignId ? (
              <div role="tablist" aria-label="Dashboard view" style={{ display: 'flex', gap: 8 }}>
                {tabs.map(t => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={activeTab === t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={activeTab === t.id ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            ) : <span />}

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={includeTestLeads}
                onChange={e => setIncludeTestLeads(e.target.checked)}
              />
              Show test leads
            </label>
          </div>

          {(activeTab === 'affiliate' || activeTab === 'campaigns') && (
            campaignId
              ? (
                <RolePanel
                  role="affiliate"
                  campaignId={campaignId}
                  includeTestLeads={includeTestLeads}
                  onBack={() => { setActiveTab('campaigns'); navigate('/partner-dashboard') }}
                />
              )
              : (
                <CampaignListView
                  includeTestLeads={includeTestLeads}
                  onSelect={(id) => navigate(`/partner-dashboard/campaigns/${id}`)}
                  showNotices={false}
                />
              )
          )}
          {activeTab === 'buyer' && (
            buyerCampaignId
              ? (
                <RolePanel
                  role="buyer"
                  campaignId={buyerCampaignId}
                  includeTestLeads={includeTestLeads}
                  onBack={() => navigate('/partner-dashboard')}
                />
              )
              : (
                <CampaignListView
                  endpoint="/partner/buyer/campaigns"
                  includeTestLeads={includeTestLeads}
                  onSelect={(id) => navigate(`/partner-dashboard/buyer-campaigns/${id}`)}
                />
              )
          )}
        </>
      )}
    </div>
  )
}
